import React from "react";
import { Field } from "@/pages/Leads";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Mirrors backend core.NOT_CONVERTED_REASONS (validated server-side).
export const NOT_CONVERTED_REASONS = [
  "Budget Issue", "Still Thinking", "Chose Competitor",
  "Needs Follow-up", "Not Suitable", "Other",
];

export const PAYMENT_METHODS = ["cash", "card", "upi", "bank_transfer", "cheque", "other"];

/**
 * Outcome + money for a completed visit. Shared by the Mark Visited dialog and
 * the Checkout screen so the derived-outstanding arithmetic exists in exactly
 * one place — forking it is how the two would silently disagree.
 *
 * `value` is { outcome, reason, revenue, outstanding, treatmentId, method }.
 * `packages` is the patient's packages that still carry a balance.
 * `hideMoney` — Checkout's full invoice builder supplies revenue/outstanding
 * from the bill itself, so only the outcome is asked here.
 */
export default function VisitedFields({ value, onChange, packages = [], hideMoney = false }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const notConverted = value.outcome === "not_converted";
  const selectedPkg = packages.find((p) => p.id === value.treatmentId);
  const cash = parseFloat(value.revenue) || 0;
  // Derived, never typed: what they still owe on the package after today's cash.
  const derivedOutstanding = selectedPkg
    ? Math.max(0, Math.round((selectedPkg.ledger.balance_due - cash) * 100) / 100)
    : null;

  return (
    <>
      {/* Outcome: converted (bought) vs not-converted (came but didn't buy). */}
      <Field label="Outcome">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => set("outcome", "converted")}
            className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${!notConverted ? "border-green-500 bg-green-50 text-green-800" : "border-input text-muted-foreground hover:bg-nude/20"}`}
            data-testid="visited-outcome-converted"
          >
            Converted
          </button>
          <button
            type="button"
            onClick={() => set("outcome", "not_converted")}
            className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${notConverted ? "border-red-400 bg-red-50 text-red-700" : "border-input text-muted-foreground hover:bg-nude/20"}`}
            data-testid="visited-outcome-not-converted"
          >
            Not Converted
          </button>
        </div>
      </Field>

      {notConverted && (
        <Field label="Reason (optional)">
          <Select value={value.reason} onValueChange={(v) => set("reason", v)}>
            <SelectTrigger data-testid="visited-reason"><SelectValue placeholder="Why didn't they convert?" /></SelectTrigger>
            <SelectContent>{NOT_CONVERTED_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">Patient stays active as a warm lead for follow-up.</p>
        </Field>
      )}

      {/* Package with a balance? Then Revenue is cash taken today and becomes a
          real payment against that package, and Outstanding is derived from the
          ledger rather than typed — so the two can't drift. */}
      {!hideMoney && packages.length > 0 && (
        <Field label="Bill against package">
          <Select
            value={value.treatmentId || "__none__"}
            onValueChange={(v) => set("treatmentId", v === "__none__" ? "" : v)}
          >
            <SelectTrigger data-testid="visited-package"><SelectValue placeholder="Not a package visit" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not a package visit</SelectItem>
              {packages.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.package_name || p.name} · {p.sessions_done}/{p.sessions_total} · ₹{Number(p.ledger.balance_due).toLocaleString("en-IN")} due
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      {!hideMoney && <div className="grid grid-cols-2 gap-2">
        <Field label={selectedPkg ? "Collected today" : "Revenue"}>
          <Input
            type="number" inputMode="decimal" value={value.revenue}
            onChange={(e) => set("revenue", e.target.value)}
            placeholder="₹ 0" data-testid="visited-revenue" autoFocus={!notConverted}
          />
        </Field>
        <Field label="Outstanding">
          {selectedPkg ? (
            <div
              className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground"
              title="Calculated from the package balance"
              data-testid="visited-outstanding-derived"
            >
              ₹{Number(derivedOutstanding).toLocaleString("en-IN")}
            </div>
          ) : (
            <Input
              type="number" inputMode="decimal" value={value.outstanding}
              onChange={(e) => set("outstanding", e.target.value)}
              placeholder="₹ 0" data-testid="visited-outstanding"
            />
          )}
        </Field>
      </div>}

      {!hideMoney && selectedPkg && (
        <Field label="Payment method">
          <Select value={value.method} onValueChange={(v) => set("method", v)}>
            <SelectTrigger data-testid="visited-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map((m) => (
                <SelectItem key={m} value={m}>{m.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            Recorded as a payment on {selectedPkg.package_name || selectedPkg.name} — no need to raise an invoice separately.
          </p>
        </Field>
      )}
    </>
  );
}

/** The PUT body for marking an appointment visited, shared by both callers. */
export function visitedPayload(value, { note } = {}) {
  const selectedPackage = !!value.treatmentId;
  const body = {
    status: "visited",
    visit_outcome: value.outcome,
    revenue: value.revenue !== "" ? parseFloat(value.revenue) : null,
    outstanding: value.outstanding !== "" ? parseFloat(value.outstanding) : null,
  };
  if (selectedPackage) {
    // The backend turns `revenue` into a real payment on the package and
    // computes `outstanding` itself, so don't send a typed one.
    body.treatment_id = value.treatmentId;
    body.payment_method = value.method;
    delete body.outstanding;
  }
  if (value.outcome === "not_converted" && value.reason) body.not_converted_reason = value.reason;
  if (note && note.trim()) body.notes = note.trim();
  return body;
}
